import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
	CardType,
	type AnalysisCardConfig,
	type Analysis,
} from '../../../models/card-model';
import { DataService } from '../../../services/data.service';
import { JeptoBaseCardComponent } from '../jepto-base-card.component';
import { JeptoAnalysisDrawerComponent } from './jepto-analysis-drawer/jepto-analysis-drawer.component';
import { JeptoUiProgressBarIndeterminateComponent } from '../../../../ui-lib/buttons-indicators/jepto-ui-progress-bar-indeterminate/jepto-ui-progress-bar-indeterminate.component';
import { analysisCardFaceHeadline } from '../../../utils/analysis-card-headline.util';
import { getAnalysisPickerLabel } from '../../../utils/analysis-label.util';
import { resolveAnalyses } from '../../../utils/resolve-analyses.util';
import {
	publishedAnalysiss,
	analysisKey,
	analysisKeysMatch,
} from '../../../utils/analysis.util';

/** Grid cards stop waiting on a silent WebSocket so first paint cannot spin forever. */
export const ANALYSIS_CARD_WS_HYDRATION_TIMEOUT_MS = 8_000;

@Component({
	selector: 'jepto-analysis-card',
	templateUrl: './jepto-analysis-card.component.html',
	styleUrl: './jepto-analysis-card.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		class: 'jepto-analysis-card',
		'[class.jepto-analysis-card--edit-mode]': 'isEditMode()',
		'[class.jepto-analysis-card--drawer-open]': 'drawerOpen()',
		'[class.jepto-analysis-card--contained-overlay]':
			'drawerOverlay() === "contained"',
	},
	imports: [
		MatIconModule,
		MatMenuModule,
		JeptoAnalysisDrawerComponent,
		JeptoUiProgressBarIndeterminateComponent,
	],
})
export class JeptoAnalysisCardComponent extends JeptoBaseCardComponent {
	private static readonly OVERLAY_OPEN_CLASS =
		'analysis-overlay-open-grid-item';
	private static readonly AUTO_ROTATE_INTERVAL_MS = 7000;
	private static readonly FADE_OUT_MS = 220;
	private static readonly FADE_IN_MS = 260;

	readonly testId = input<string | undefined>();
	/** Shown on the card face when there is no analysis period content (not while loading). */
	readonly emptyMessage = input('No analysis available.');
	analyses = input<Analysis[]>([]);
	/** Optional override for which analysis period is active (e.g. editor preview). */
	selectedPeriodId = input<string | undefined>(undefined);
	loading = input<boolean>(false);
	/**
	 * When true (report grid), missing WebSocket period data shows a loader until
	 * hydration or {@link ANALYSIS_CARD_WS_HYDRATION_TIMEOUT_MS}. Editor preview
	 * sets false so an explicit empty draft shows empty state.
	 */
	awaitWebSocketHydration = input(true);
	/** Where the full analysis drawer overlays — use `contained` in editor preview panes. */
	drawerOverlay = input<'viewport' | 'contained'>('viewport');
	/** Portal mount for contained drawer (editor `.lce-preview__drawer-root`). */
	drawerOverlayHost = input<HTMLElement | null>(null);
	/** Optional drawer panel width CSS value. */
	drawerPanelWidth = input<string | undefined>(undefined);
	/** Report preview: hide draft periods; editor and edit grid leave this false. */
	publishedOnly = input(false);
	/**
	 * Theme-template previews keep the Open full analysis button visible for styling
	 * but must not open the drawer.
	 */
	openAnalysisInteractive = input(true);

	private readonly dataService = inject(DataService, { optional: true });

	private openBtnRef =
		viewChild<ElementRef<HTMLButtonElement>>('openAnalysisBtn');

	protected drawerOpen = signal(false);
	protected fadeOutHeadline = signal(false);
	protected fadeInHeadline = signal(false);
	private readonly _wsHydrationTimedOut = signal(false);
	private activePeriodId = signal<string | undefined>(undefined);
	private elevatedGridItem: HTMLElement | null = null;
	private originalGridItemZIndex = '';
	private fadeOutTimeoutId: ReturnType<typeof globalThis.setTimeout> | null =
		null;
	private fadeInTimeoutId: ReturnType<typeof globalThis.setTimeout> | null =
		null;

	protected effectiveTestId = computed(
		() =>
			this.testId() ??
			(this.baseConfig() as AnalysisCardConfig | undefined)?.cardId ??
			this.cardId() ??
			'analysis-card'
	);

	protected effectivePeriods = computed(() => {
		const fromInput = this.analyses();
		const cfg = this.baseConfig() as AnalysisCardConfig | null | undefined;
		const cardId = this.cardId() ?? cfg?.cardId;
		const wsPeriods =
			cardId && this.dataService
				? this.dataService.analysesDataSignal(cardId)()
				: undefined;

		const configForResolve: AnalysisCardConfig = cfg ?? {
			cardId: cardId ?? '',
			cardType: CardType.Analysis,
			analyses: fromInput,
			pageId: '',
			viewId: '',
			clientId: '',
		};

		const mergedConfig: AnalysisCardConfig =
			(configForResolve.analyses?.length ?? 0) > 0
				? configForResolve
				: { ...configForResolve, analyses: fromInput };

		const merged = resolveAnalyses(mergedConfig, wsPeriods);
		return this.publishedOnly() ? publishedAnalysiss(merged) : merged;
	});

	protected displayPeriods = computed(() =>
		this.effectivePeriods().filter(period =>
			this.#hasDisplayablePeriod(period)
		)
	);

	protected effectiveLoading = computed(() => {
		if (this.loading()) return true;
		if (!this.awaitWebSocketHydration()) return false;
		if (this._wsHydrationTimedOut()) return false;
		const cardId = this.cardId();
		if (!cardId || !this.dataService) {
			return false;
		}
		const wsPeriods = this.dataService.analysesDataSignal(cardId)();
		return wsPeriods === undefined && this.effectivePeriods().length === 0;
	});

	private readonly autoRotationEnabled = computed(
		() =>
			this.displayPeriods().length >= 2 &&
			!this.drawerOpen() &&
			!this.effectiveLoading()
	);

	protected activePeriod = computed<Analysis | null>(() => {
		const periods = this.displayPeriods();
		if (!periods.length) return null;

		const activeId = this.activePeriodId();
		if (activeId) {
			const byId = periods.find(
				period => analysisKey(period) === activeId
			);
			if (byId) return byId;
		}

		const selectedId = this.selectedPeriodId();
		if (selectedId) {
			const bySelectedId = periods.find(
				period => analysisKey(period) === selectedId
			);
			if (bySelectedId) return bySelectedId;
		}

		return periods[0] ?? null;
	});

	protected activePeriodIndex = computed(() => {
		const period = this.activePeriod();
		if (!period) return -1;
		return this.displayPeriods().findIndex(item =>
			analysisKeysMatch(item, period)
		);
	});

	protected hasContent = computed(
		() => !!this.activePeriod()?.content?.trim()
	);

	protected activePeriodHeadline = computed(() => {
		const period = this.activePeriod();
		if (!period) return '';
		return analysisCardFaceHeadline(period);
	});

	constructor() {
		super();

		effect(onCleanup => {
			if (!this.awaitWebSocketHydration()) {
				untracked(() => this._wsHydrationTimedOut.set(false));
				return;
			}
			const cardId = this.cardId();
			if (!cardId || !this.dataService) {
				untracked(() => this._wsHydrationTimedOut.set(false));
				return;
			}
			const wsPeriods = this.dataService.analysesDataSignal(cardId)();
			if (wsPeriods !== undefined) {
				untracked(() => this._wsHydrationTimedOut.set(false));
				return;
			}

			untracked(() => this._wsHydrationTimedOut.set(false));
			const timerId = globalThis.setTimeout(() => {
				this._wsHydrationTimedOut.set(true);
			}, ANALYSIS_CARD_WS_HYDRATION_TIMEOUT_MS);
			onCleanup(() => globalThis.clearTimeout(timerId));
		});

		effect(onCleanup => {
			if (!this.autoRotationEnabled()) return;

			// The tick body writes signals, which schedule CD themselves.
			const timerId = globalThis.setInterval(() => {
				this.#selectNextPeriodWithFade();
			}, JeptoAnalysisCardComponent.AUTO_ROTATE_INTERVAL_MS);

			onCleanup(() => {
				globalThis.clearInterval(timerId);
				this.#clearFadeTimers();
			});
		});
	}

	protected openDrawer(): void {
		if (!this.openAnalysisInteractive()) {
			return;
		}
		if (this.drawerOverlay() === 'viewport') {
			this.#elevateGridItem();
		}
		this.drawerOpen.set(true);
	}

	protected closeDrawer(): void {
		this.drawerOpen.set(false);
		if (this.drawerOverlay() === 'viewport') {
			this.#resetGridItemElevation();
		}
	}

	protected get triggerElement(): HTMLElement | null {
		return this.openBtnRef()?.nativeElement ?? null;
	}

	protected selectPeriod(periodKey: string): void {
		if (
			!this.displayPeriods().some(
				period => analysisKey(period) === periodKey
			)
		) {
			return;
		}
		this.#clearFadeTimers();
		this.activePeriodId.set(periodKey);
	}

	protected periodLabel(period: Analysis, index: number): string {
		return getAnalysisPickerLabel(period, index);
	}

	protected periodKey(period: Analysis): string {
		return analysisKey(period);
	}

	#selectNextPeriodWithFade(): void {
		if (this.fadeOutHeadline() || this.fadeInHeadline()) return;

		this.#clearFadeTimers();
		this.fadeOutHeadline.set(true);

		this.fadeOutTimeoutId = globalThis.setTimeout(() => {
			this.#selectNextPeriod();
			this.fadeOutHeadline.set(false);
			this.fadeInHeadline.set(true);

			this.fadeInTimeoutId = globalThis.setTimeout(() => {
				this.fadeInHeadline.set(false);
				this.fadeInTimeoutId = null;
			}, JeptoAnalysisCardComponent.FADE_IN_MS);
			this.fadeOutTimeoutId = null;
		}, JeptoAnalysisCardComponent.FADE_OUT_MS);
	}

	#selectNextPeriod(): void {
		const periods = this.displayPeriods();
		if (periods.length < 2) return;

		const currentIndex = this.activePeriodIndex();
		const nextIndex =
			currentIndex < 0 ? 0 : (currentIndex + 1) % periods.length;
		this.activePeriodId.set(analysisKey(periods[nextIndex]));
	}

	#hasDisplayablePeriod(period: Analysis): boolean {
		return (
			analysisCardFaceHeadline(period).length > 0 ||
			!!period.content?.trim()
		);
	}

	#clearFadeTimers(): void {
		if (this.fadeOutTimeoutId !== null) {
			globalThis.clearTimeout(this.fadeOutTimeoutId);
			this.fadeOutTimeoutId = null;
		}
		if (this.fadeInTimeoutId !== null) {
			globalThis.clearTimeout(this.fadeInTimeoutId);
			this.fadeInTimeoutId = null;
		}
		this.fadeOutHeadline.set(false);
		this.fadeInHeadline.set(false);
	}

	#elevateGridItem(): void {
		const hostEl = this.elementRef.nativeElement as HTMLElement;
		const gridItem = hostEl.closest(
			'.grid-stack-item'
		) as HTMLElement | null;
		if (!gridItem) return;

		if (this.elevatedGridItem !== gridItem) {
			this.elevatedGridItem = gridItem;
			this.originalGridItemZIndex = gridItem.style.zIndex;
		}

		gridItem.style.zIndex = '10000';
		gridItem.classList.add(JeptoAnalysisCardComponent.OVERLAY_OPEN_CLASS);
	}

	#resetGridItemElevation(): void {
		if (!this.elevatedGridItem) return;

		if (this.originalGridItemZIndex) {
			this.elevatedGridItem.style.zIndex = this.originalGridItemZIndex;
		} else {
			this.elevatedGridItem.style.removeProperty('z-index');
		}
		this.elevatedGridItem.classList.remove(
			JeptoAnalysisCardComponent.OVERLAY_OPEN_CLASS
		);

		this.elevatedGridItem = null;
		this.originalGridItemZIndex = '';
	}
}
